"use client"

import { useState, useRef, useEffect } from "react"
import {
  ChevronDown,
  Utensils,
  Bed,
  Camera,
  ShoppingBag,
  Leaf,
  Plane,
  Coffee,
  Briefcase,
  Wine,
} from "lucide-react"
import "./TypeFilter.css"

const TypeFilter = ({ activeFilter, onFilterChange, places, placeTypes }) => {
  console.log("🏷️ TypeFilter rendered with:", {
    activeFilter,
    placesCount: places.length,
    placeTypesCount: Object.keys(placeTypes).length,
    placeTypes: placeTypes,
  })

  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  // Available filter types with icons
  const allFilterTypes = [
    { id: "all", label: "All Places", isText: true },
    { id: "restaurant", label: "Restaurants", icon: Utensils, color: "#ff6b35" },
    { id: "activity", label: "Activities", icon: Camera, color: "#4ecdc4" },
    { id: "lodging", label: "Lodging", icon: Bed, color: "#45b7d1" },
    { id: "nature", label: "Nature", icon: Leaf, color: "#4caf50" },
    { id: "airport", label: "Airport", icon: Plane, color: "#9c27b0" },
    { id: "coffee", label: "Coffee", icon: Coffee, color: "#ff9800" },
    { id: "luggage", label: "Luggage", icon: Briefcase, color: "#00bdff" },
    { id: "drinks", label: "Drinks", icon: Wine, color: "#e91e63" },
    { id: "shopping", label: "Shopping", icon: ShoppingBag, color: "#ff4081" },
  ]

  // Get available types from ALL places (not filtered places)
  const getAvailableTypes = () => {
    const types = new Set()
    places.forEach((place) => {
      const key = `${place.lat},${place.lng}`
      const placeType = placeTypes[key] || place.type
      if (placeType) {
        types.add(placeType)
        console.log(`🏷️ Found type "${placeType}" for place: ${place.name}`)
      }
    })
    console.log("🏷️ Available types:", Array.from(types))
    return types
  }

  const availableTypes = getAvailableTypes()

  // Always include "all" option, then only include types that exist
  const availableFilterTypes = [allFilterTypes[0]] // "All Places"

  allFilterTypes.slice(1).forEach((filterType) => {
    if (availableTypes.has(filterType.id)) {
      availableFilterTypes.push(filterType)
    }
  })

  // Close dropdown when clicking outside
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Always show the filter if there are any place types at all (changed from <= 2 to <= 1)
  if (availableFilterTypes.length <= 1) {
    return null
  }

  const handleFilterChange = (type) => {
    onFilterChange(type)
    setIsOpen(false) // Close dropdown after selection
  }

  // Get current filter from available types
  const currentFilter = availableFilterTypes.find((type) => type.id === activeFilter) || availableFilterTypes[0]

  return (
    <div className="clean-filter-dropdown" ref={dropdownRef}>
      <button
        className="clean-filter-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`Filter by ${currentFilter.label}`}
        title={currentFilter.label}
      >
        {currentFilter.isText ? (
          <span className="filter-text">All</span>
        ) : (
          <currentFilter.icon size={16} className="filter-icon" />
        )}
        <ChevronDown size={14} className={`chevron ${isOpen ? "open" : ""}`} />
      </button>

      {isOpen && (
        <div className="clean-filter-options">
          {availableFilterTypes.map((type) => (
            <button
              key={type.id}
              className={`clean-filter-icon ${activeFilter === type.id ? "active" : ""}`}
              onClick={() => handleFilterChange(type.id)}
              title={type.label}
              aria-label={type.label}
            >
              {type.isText ? <span className="all-text">All</span> : <type.icon size={18} />}
            </button>
          ))}

          {/* Add a clear filter option if not on "all" */}
          {activeFilter !== "all" && (
            <button
              className="clean-filter-icon clear-filter"
              onClick={() => handleFilterChange("all")}
              title="Show All Places"
              aria-label="Show All Places"
            >
              <span className="clear-text">✕</span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default TypeFilter
